import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

/** Job status values */
type JobStatus = 'pending' | 'processing' | 'completed' | 'failed';

/** Internal job representation */
interface QueueJob {
  id: string;
  queue: string;
  payload: unknown;
  priority: number;
  status: JobStatus;
  enqueuedAt: number;
  delay: number;
  result?: unknown;
  error?: string;
}

/**
 * Queue integration with in-memory backend.
 *
 * Provides job queue semantics: enqueue, dequeue, status tracking,
 * completion, failure, cancellation, and size queries.
 */
export class QueueIntegration extends BaseIntegration {
  /** Map from queue name to ordered list of job IDs */
  private queues: Map<string, string[]> = new Map();

  /** Map from job ID to job data */
  private jobs: Map<string, QueueJob> = new Map();

  constructor(config: IntegrationConfig) {
    super(config);
    this.logger.info('Queue integration initialized (in-memory backend)');
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();

    try {
      let data: unknown;

      switch (action) {
        case 'enqueue':
          data = await this.executeWithRetry(() => this.enqueue(params));
          break;
        case 'dequeue':
          data = await this.executeWithRetry(() => this.dequeue(params));
          break;
        case 'status':
          data = await this.executeWithRetry(() => this.status(params));
          break;
        case 'complete':
          data = await this.executeWithRetry(() => this.complete(params));
          break;
        case 'fail':
          data = await this.executeWithRetry(() => this.failJob(params));
          break;
        case 'cancel':
          data = await this.executeWithRetry(() => this.cancel(params));
          break;
        case 'size':
          data = await this.executeWithRetry(() => this.size(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  /** Generate a unique job identifier. */
  private generateJobId(): string {
    return `job_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  /** Get or create the queue array for a given queue name. */
  private getQueue(name: string): string[] {
    let queue = this.queues.get(name);
    if (!queue) {
      queue = [];
      this.queues.set(name, queue);
    }
    return queue;
  }

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------

  private async enqueue(
    params: IntegrationParams,
  ): Promise<{ jobId: string; position: number }> {
    const queueName = params.queue as string;
    const payload = params.payload;
    const delay = (params.delay as number | undefined) ?? 0;
    const priority = (params.priority as number | undefined) ?? 0;

    this.logger.debug('Queue ENQUEUE', { queue: queueName, delay, priority });

    const jobId = this.generateJobId();
    const job: QueueJob = {
      id: jobId,
      queue: queueName,
      payload,
      priority,
      status: 'pending',
      enqueuedAt: Date.now(),
      delay,
    };

    this.jobs.set(jobId, job);

    const queue = this.getQueue(queueName);

    // Insert respecting priority (higher priority = earlier in queue)
    let insertIndex = queue.length;
    for (let i = 0; i < queue.length; i++) {
      const existingJob = this.jobs.get(queue[i]);
      if (existingJob && existingJob.priority < priority) {
        insertIndex = i;
        break;
      }
    }
    queue.splice(insertIndex, 0, jobId);

    return { jobId, position: insertIndex };
  }

  private async dequeue(
    params: IntegrationParams,
  ): Promise<{ job: { id: string; payload: unknown; enqueuedAt: number; priority: number } | null }> {
    const queueName = params.queue as string;

    this.logger.debug('Queue DEQUEUE', { queue: queueName });

    const queue = this.getQueue(queueName);
    const now = Date.now();

    // Find first pending job whose delay has elapsed
    for (let i = 0; i < queue.length; i++) {
      const job = this.jobs.get(queue[i]);
      if (!job || job.status !== 'pending') continue;

      const readyAt = job.enqueuedAt + job.delay;
      if (now < readyAt) continue;

      // Mark as processing and remove from queue
      job.status = 'processing';
      queue.splice(i, 1);

      return {
        job: {
          id: job.id,
          payload: job.payload,
          enqueuedAt: job.enqueuedAt,
          priority: job.priority,
        },
      };
    }

    return { job: null };
  }

  private async status(
    params: IntegrationParams,
  ): Promise<{ status: JobStatus; job: { id: string; payload: unknown; enqueuedAt: number; priority: number } | null }> {
    const jobId = params.jobId as string;

    this.logger.debug('Queue STATUS', { jobId });

    const job = this.jobs.get(jobId);
    if (!job) {
      return { status: 'pending', job: null };
    }

    return {
      status: job.status,
      job: {
        id: job.id,
        payload: job.payload,
        enqueuedAt: job.enqueuedAt,
        priority: job.priority,
      },
    };
  }

  private async complete(
    params: IntegrationParams,
  ): Promise<{ completed: boolean }> {
    const jobId = params.jobId as string;
    const result = params.result;

    this.logger.debug('Queue COMPLETE', { jobId });

    const job = this.jobs.get(jobId);
    if (!job || job.status !== 'processing') {
      return { completed: false };
    }

    job.status = 'completed';
    job.result = result;
    return { completed: true };
  }

  private async failJob(
    params: IntegrationParams,
  ): Promise<{ failed: boolean }> {
    const jobId = params.jobId as string;
    const error = params.error as string | undefined;

    this.logger.debug('Queue FAIL', { jobId });

    const job = this.jobs.get(jobId);
    if (!job || job.status !== 'processing') {
      return { failed: false };
    }

    job.status = 'failed';
    job.error = error;
    return { failed: true };
  }

  private async cancel(
    params: IntegrationParams,
  ): Promise<{ cancelled: boolean }> {
    const jobId = params.jobId as string;

    this.logger.debug('Queue CANCEL', { jobId });

    const job = this.jobs.get(jobId);
    if (!job || job.status !== 'pending') {
      return { cancelled: false };
    }

    // Remove from queue
    const queue = this.queues.get(job.queue);
    if (queue) {
      const idx = queue.indexOf(jobId);
      if (idx !== -1) {
        queue.splice(idx, 1);
      }
    }

    this.jobs.delete(jobId);
    return { cancelled: true };
  }

  private async size(
    params: IntegrationParams,
  ): Promise<{ size: number; pending: number; processing: number }> {
    const queueName = params.queue as string;

    this.logger.debug('Queue SIZE', { queue: queueName });

    let pending = 0;
    let processing = 0;

    for (const job of this.jobs.values()) {
      if (job.queue !== queueName) continue;
      if (job.status === 'pending') pending++;
      if (job.status === 'processing') processing++;
    }

    return { size: pending + processing, pending, processing };
  }
}

// Auto-register
registerIntegration('queue', QueueIntegration);
